// FILE: /src/services/axios.js

import { message } from "antd";
import axios from "axios";

const configuredBackendApi = String(
  process.env.REACT_APP_BACKEND_API || ""
)
  .trim()
  .replace(/\/+$/, "");

export const getBackendOrigin = () => {
  const fallbackOrigin =
    typeof window !== "undefined"
      ? window.location.origin
      : "";

  if (!configuredBackendApi) {
    return fallbackOrigin;
  }

  try {
    return new URL(
      configuredBackendApi,
      fallbackOrigin || undefined
    ).origin;
  } catch (_) {
    return configuredBackendApi;
  }
};

const axiosServices = axios.create({
  baseURL: configuredBackendApi || undefined,
  withCredentials: true,
});

axiosServices.interceptors.request.use(
  (config) => {
    const token =
      localStorage.getItem("user_Token") ||
      sessionStorage.getItem("user_Token");

    if (token && config.headers) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    return config;
  },
  (error) => Promise.reject(error)
);

const SAFE_ERROR_KEYS = new Set([
  "success",
  "message",
  "error",
  "code",
  "errorType",
  "feature",
  "requiredCredits",
  "remainingCredits",
  "addCreditsPath",
  "reauthRequired",
  "platform",
  "provider",
  "profileName",
  "profile",
  "accountName",
  "channelTitle",
  "retryAfterSeconds",
  "status",
  "completed",
  "phase",
  "targetEmail",
  "expiresInSeconds",
  "resendAfterSeconds",
  "transactionId",
  "creditsRefunded",
  "refundError",
  "requirements",
  "validationErrors",
  "requestId",
]);

const INTERNAL_ERROR_PATTERN =
  /(?:https?:\/\/|file:\/\/|\/Users\/|\/home\/|\/var\/|\/tmp\/|\\Users\\|localhost|127\.0\.0\.1|0\.0\.0\.0|node_modules|MongoServerError|MongooseError|CastError|ValidationError|ECONNREFUSED|ECONNRESET|ENOTFOUND|ETIMEDOUT|Traceback|SyntaxError|TypeError:|ReferenceError:|\bat\s+[^\n]+\([^)]+:\d+:\d+\))/i;

const getSafeFallbackMessage = (status) => {
  if (status === 400) {
    return "The request could not be processed. Please check your information and try again.";
  }

  if (status === 401) {
    return "Your session has expired. Please sign in again.";
  }

  if (status === 403) {
    return "You do not have permission to perform this action.";
  }

  if (status === 404) {
    return "The requested information could not be found.";
  }

  if (status === 409) {
    return "The request conflicts with the current state. Please refresh and try again.";
  }

  if (status === 413) {
    return "The submitted file or request is too large.";
  }

  if (status === 429) {
    return "Too many requests. Please wait and try again.";
  }

  return "Something went wrong. Please try again.";
};

const sanitizePublicString = (
  value,
  fallback
) => {
  const normalizedValue = String(
    value || ""
  ).trim();

  if (
    !normalizedValue ||
    normalizedValue.length > 500 ||
    INTERNAL_ERROR_PATTERN.test(
      normalizedValue
    )
  ) {
    return fallback;
  }

  return normalizedValue;
};

const sanitizePublicErrorData = (
  data,
  status
) => {
  const fallback =
    getSafeFallbackMessage(status);

  if (
    !data ||
    typeof data !== "object" ||
    Array.isArray(data)
  ) {
    return {
      success: false,
      message: fallback,
    };
  }

  if (status >= 500) {
    return {
      success: false,
      message: fallback,
      error: "Request failed",
      code: "INTERNAL_ERROR",
      requestId:
        data.requestId || null,
    };
  }

  const sanitizedData = {
    success: false,
  };

  Object.entries(data).forEach(
    ([key, value]) => {
      if (!SAFE_ERROR_KEYS.has(key)) {
        return;
      }

      if (typeof value === "string") {
        sanitizedData[key] =
          sanitizePublicString(
            value,
            fallback
          );
        return;
      }

      if (
        value === null ||
        typeof value === "boolean" ||
        typeof value === "number" ||
        Array.isArray(value)
      ) {
        sanitizedData[key] = value;
      }
    }
  );

  if (
    !sanitizedData.message &&
    !sanitizedData.error
  ) {
    sanitizedData.message = fallback;
  }

  return sanitizedData;
};

const createPublicApiError = (
  originalError
) => {
  const status =
    originalError.response?.status ||
    null;

  const data =
    sanitizePublicErrorData(
      originalError.response?.data,
      status
    );

  const publicError = new Error(
    data.message ||
      data.error ||
      getSafeFallbackMessage(status)
  );

  publicError.name = "PublicApiError";
  publicError.isAxiosError = true;
  publicError.code =
    data.code || null;
  publicError.status = status;
  publicError.response = {
    status,
    statusText: "",
    headers: {
      "retry-after":
        originalError.response
          ?.headers?.["retry-after"],
    },
    data,
  };

  return publicError;
};

axiosServices.interceptors.response.use(
  (response) => response,
  (error) => {
    const response = error.response;
    const responseData =
      sanitizePublicErrorData(
        response?.data,
        response?.status || null
      );

    if (response) {
      response.data = responseData;
    }

    if (response?.status === 401) {
      localStorage.removeItem(
        "user_Token"
      );
      sessionStorage.removeItem(
        "user_Token"
      );
      window.location.href = "/";
    }

    if (
      response?.status === 409 &&
      responseData.errorType ===
        "TOKEN_INVALID"
    ) {
      const platform =
        responseData.platform ||
        responseData.provider ||
        "social media";

      const profileName =
        responseData.profileName ||
        responseData.profile ||
        responseData.accountName ||
        responseData.channelTitle ||
        null;

      const connectionLabel = profileName
        ? `${platform} connection for "${profileName}"`
        : `${platform} connection`;

      message.error(
        `Your ${connectionLabel} has expired. Please reconnect it from Brands & Channels.`,
        5
      );
    }

    return Promise.reject(
      createPublicApiError(error)
    );
  }
);

export default axiosServices;